import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Form, InputGroup, Button, Spinner, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faGlobe, faLocationDot, faFloppyDisk } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import { LoginManager } from '../../core/login-manager';
import { toast } from 'react-toastify';
import { LanguageProvider } from '../../core/language-provider';

export default function Settings() {
    const blue = '#0A2B5E';
    const navigate = useNavigate();

    const userInfo = LoginManager.getUserInfo();
    const [notifyApps, setNotifyApps] = useState(!!userInfo?.notificationsStatusJob);
    const [notifyNew, setNotifyNew] = useState(!!userInfo?.notificationsNewJob);

    const [form, setForm] = useState({
        name: userInfo && userInfo.name,
        surname: userInfo && userInfo.surname,
        email: userInfo && userInfo.email,
        phone: userInfo && userInfo.phone,
        nation: userInfo && userInfo.country,
        province: userInfo && userInfo.province,
        notificationStatus: !!userInfo?.notificationsStatusJob, // candidature inviate
        notificationNew: !!userInfo?.notificationsNewJob,      // nuove posizioni
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [saving, setSaving] = useState(false);
    const [changingPwd, setChangingPwd] = useState(false);
    const [showPwdModal, setShowPwdModal] = useState(false);

    useEffect(() => {
        console.log(userInfo)
    }, [])

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleLogout = async () => {
        try {
            await LoginManager.logout();
        } finally {
            navigate('/login');
        }
    }

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const body = {
                name: form.name ?? '',
                surname: form.surname ?? '',
                email: form.email ?? '',
                phone: form.phone ?? '',
                country: form.nation ?? '',
                province: form.province ?? '',
                notificationsStatusJob: !!form.notificationStatus,
                notificationsNewJob: !!form.notificationNew,
            };
            const res = await LoginManager.updateProfile(body);
            if (res && res.success === 1) {
                // Reload session/user info to reflect changes in the app
                await LoginManager.refreshSid();
                toast.success(LanguageProvider.get('pages.profileSettings.general_success') || 'Impostazioni salvate');
            } else {
                toast.error(LanguageProvider.get('pages.profileSettings.errors?.update') || 'Errore durante il salvataggio');
            }
        } catch (err) {
            console.error('handleSave error', err);
            toast.error('Errore durante il salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async () => {
        if (changingPwd) return;
        const { currentPassword, newPassword, confirmPassword } = form;
        if (!currentPassword || !newPassword || !confirmPassword) {
            toast.error(LanguageProvider.get('pages.defaults.error') || 'Compila tutti i campi');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error(LanguageProvider.get('pages.profileSettings.passwords_match_error') || 'Le password non coincidono');
            return;
        }
        setChangingPwd(true);
        try {
            const res = await LoginManager.changePassword(currentPassword, newPassword, confirmPassword);
            if (res && res.success === 1) {
                toast.success(LanguageProvider.get('pages.profileSettings.security_success') || 'Password modificata con successo');
                setForm(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
            } else {
                toast.error(LanguageProvider.get('pages.defaults.error') || 'Errore durante la modifica della password');
            }
        } catch (err) {
            console.error('handleChangePassword error', err);
            toast.error(LanguageProvider.get('pages.defaults.error') || 'Errore durante la modifica della password');
        } finally {
            setChangingPwd(false);
        }
    };

    return (
        <Container fluid className='profile-settings'>
            <Container fluid={'md'}>
                {/* Title */}
                <Row className='pt-5 pb-3 align-items-center mb-5'>
                    <Col>
                        <Link to='/profile' style={{ color: blue, textDecoration: 'none' }}>
                            <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
                            <span className='visually-hidden'>{LanguageProvider.get('pages.profileSettings.back')}</span>
                        </Link>
                        <h2 className='d-inline ms-2' style={{ color: blue }}>{LanguageProvider.get('pages.profileSettings.title')}</h2>
                    </Col>
                </Row>

                {/* Notifications */}
                <Row className='align-items-center mb-5'>
                    <Col md={2} xs={12} className='text-md-start text-center fw-semibold' style={{ color: blue }}>{LanguageProvider.get('pages.profileSettings.notifications')}</Col>
                    <Col md={5} xs={12} className='mb-2 mb-md-0'>
                        <Form.Check
                            type='switch'
                            id='switch-apps'
                            label={LanguageProvider.get('pages.profileSettings.switches.apps')}
                            checked={notifyApps}
                            onChange={(e) => {
                                const v = e.target.checked;
                                setNotifyApps(v);
                                setForm(prev => ({ ...prev, notificationStatus: v }));
                            }}
                        />
                    </Col>
                    <Col md={5} xs={12}>
                        <Form.Check
                            type='switch'
                            id='switch-new'
                            label={LanguageProvider.get('pages.profileSettings.switches.new')}
                            checked={notifyNew}
                            onChange={(e) => {
                                const v = e.target.checked;
                                setNotifyNew(v);
                                setForm(prev => ({ ...prev, notificationNew: v }));
                            }}
                        />
                    </Col>
                </Row>

                <hr className='my-5' />

                {/* Personal Data */}
                <Row className='align-items-center mt-5 mb-4'>
                    <Col md={2} xs={12} className='text-md-start text-center fw-semibold' style={{ color: blue }}>{LanguageProvider.get('pages.profileSettings.yourData')}</Col>
                    <Col md={10}>
                        <Row className='g-4'>
                            <Col md={4}><Form.Control value={form.name} onChange={set('name')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.name')} /></Col>
                            <Col md={4}><Form.Control value={form.surname} onChange={set('surname')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.surname')} /></Col>
                            <Col md={4}><Form.Control value={form.email} onChange={set('email')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.email')} /></Col>

                            <Col md={4}><Form.Control value={form.phone} onChange={set('phone')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.phone')} /></Col>
                            <Col md={4}>
                                <InputGroup>
                                    <InputGroup.Text style={{ background: 'transparent', borderRight: 'none' }}>
                                        <FontAwesomeIcon icon={faGlobe} />
                                    </InputGroup.Text>
                                    <Form.Control style={{ borderLeft: 'none' }} value={form.nation} onChange={set('nation')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.nation')} />
                                </InputGroup>
                            </Col>
                            <Col md={4}>
                                <InputGroup>
                                    <InputGroup.Text style={{ background: 'transparent', borderRight: 'none' }}>
                                        <FontAwesomeIcon icon={faLocationDot} />
                                    </InputGroup.Text>
                                    <Form.Control style={{ borderLeft: 'none' }} value={form.province} onChange={set('province')} placeholder={LanguageProvider.get('pages.profileSettings.placeholders.province')} />
                                </InputGroup>
                            </Col>

                            {/* Password fields moved to modal */}

                            {/* Actions: move Change Password here and add more spacing */}
                            <Col xs={12} className='mt-4'>
                                <Button variant='outline-secondary' className='w-100' onClick={() => setShowPwdModal(true)}>
                                    {LanguageProvider.get('pages.profileSettings.buttons.changePassword')}
                                </Button>
                            </Col>
                            <Col xs={12} className='mt-4'>
                                <Button className='w-100' style={{ backgroundColor: '#2FAAE0', borderColor: '#2FAAE0' }} onClick={handleSave} disabled={saving}>
                                    {saving ? (
                                        <>
                                            {LanguageProvider.get('defaults.loading') || 'Saving…'} <Spinner size='sm' className='ms-2' />
                                        </>
                                    ) : (
                                        <>
                                            {LanguageProvider.get('pages.profileSettings.buttons.save')} <FontAwesomeIcon icon={faFloppyDisk} className='ms-2' />
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                <hr className='my-5' />

                {/* Account actions */}
                <Row className='align-items-center mt-4 mb-5'>
                    <Col md={2} xs={12} className='text-md-start text-center fw-semibold' style={{ color: blue }}>{LanguageProvider.get('pages.profileSettings.logout')}</Col>
                    <Col md={10}>
                        <Row>
                            <Col xs={12}>
                                <Button variant='outline-danger' className='w-100' onClick={handleLogout}>
                                    {LanguageProvider.get('pages.profileSettings.buttons.logout')}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>

                {/* Change Password Modal */}
                <Modal show={showPwdModal} onHide={() => setShowPwdModal(false)} centered>
                    <Modal.Body className='p-4 p-md-5'>
                        <h2 className='fw-bold mb-3' style={{ color: blue, lineHeight: 1.2 }}>
                            {LanguageProvider.get('pages.profileSettings.passwordModal.title') || LanguageProvider.get('pages.profileSettings.buttons.changePassword')}
                        </h2>

                        <Row className='g-3'>
                            <Col xs={12}>
                                <Form.Control
                                    type='password'
                                    placeholder={LanguageProvider.get('pages.profileSettings.placeholders.currentPassword')}
                                    value={form.currentPassword}
                                    onChange={set('currentPassword')}
                                />
                            </Col>
                            <Col xs={12}>
                                <Form.Control
                                    type='password'
                                    placeholder={LanguageProvider.get('pages.profileSettings.placeholders.newPassword')}
                                    value={form.newPassword}
                                    onChange={set('newPassword')}
                                />
                            </Col>
                            <Col xs={12}>
                                <Form.Control
                                    type='password'
                                    placeholder={LanguageProvider.get('pages.profileSettings.placeholders.confirmPassword')}
                                    value={form.confirmPassword}
                                    onChange={set('confirmPassword')}
                                />
                            </Col>
                            <Col xs={12} className='mt-2 d-grid gap-2'>
                                <Button
                                    variant='outline-secondary'
                                    onClick={() => setShowPwdModal(false)}
                                    disabled={changingPwd}
                                >
                                    {LanguageProvider.get('defaults.close') || 'Close'}
                                </Button>
                                <Button
                                    style={{ backgroundColor: '#2FAAE0', borderColor: '#2FAAE0' }}
                                    onClick={async () => { await handleChangePassword(); if (!changingPwd) setShowPwdModal(false); }}
                                    disabled={changingPwd}
                                >
                                    {changingPwd ? (
                                        <>
                                            {LanguageProvider.get('defaults.loading') || 'Changing…'} <Spinner size='sm' className='ms-2' />
                                        </>
                                    ) : (
                                        LanguageProvider.get('pages.profileSettings.buttons.changePassword')
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Modal.Body>
                </Modal>
            </Container>
        </Container>
    );
}
